import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Plus, Minus, ShoppingBag, ImageIcon, Trash2, Printer, Banknote, X, UserPlus, Check, Search } from "lucide-react";
import { listMenuCategories, listMenuItems, createStaffOrder, listCustomers, createCustomer } from "@veorest/api-client";
import { formatCurrency } from "@veorest/utils";
import type { MenuCategoryRecord, MenuItemRecord, CustomerRecord } from "@veorest/utils";
import { useQuery } from "@tanstack/react-query";
import { useTenant } from "../hooks/useTenant";
import { useI18n } from "../i18n";
import { useBackendReachable, queueAction, saveCart, clearCart, cacheMenuItems, cacheCategories, getCachedMenuItemsSync, getCachedCategoriesSync, getCartSync } from "@veorest/pwa";

/* ── Procedural click sound (zero external files) ── */
let _ctx: AudioContext | null = null;
function beep() {
  try {
    if (!_ctx) _ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    if (_ctx.state === "suspended") _ctx.resume();
    const o = _ctx.createOscillator();
    const g = _ctx.createGain();
    o.type = "sine"; o.frequency.value = 800;
    g.gain.setValueAtTime(0.12, _ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, _ctx.currentTime + 0.06);
    o.connect(g); g.connect(_ctx.destination);
    o.start(); o.stop(_ctx.currentTime + 0.06);
  } catch { /* silence */ }
}

interface CartLine {
  menu_item_id: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  image_url?: string | null;
}

export default function PosPage() {
  const { t } = useI18n();
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? "";
  const currency = tenant?.currency ?? "IQD";

  const catsQuery = useQuery({
    queryKey: ["pos-cats", tenantId], queryFn: () => listMenuCategories(tenantId), enabled: Boolean(tenantId), staleTime: 60_000,
    initialData: () => {
      if (!tenantId) return undefined;
      const cached = getCachedCategoriesSync(tenantId);
      return cached.length > 0 ? (cached as unknown as MenuCategoryRecord[]) : undefined;
    },
  });
  const itemsQuery = useQuery({
    queryKey: ["pos-items", tenantId], queryFn: () => listMenuItems(tenantId), enabled: Boolean(tenantId), staleTime: 60_000,
    initialData: () => {
      if (!tenantId) return undefined;
      const cached = getCachedMenuItemsSync(tenantId);
      return cached.length > 0 ? (cached as unknown as MenuItemRecord[]) : undefined;
    },
  });
  // Always fall back to sync cache — categories and menu MUST render offline.
  // Computed directly (not useMemo) so async sync-cache updates are always reflected.
  const categories: MenuCategoryRecord[] =
    (catsQuery.data && catsQuery.data.length > 0) ? catsQuery.data :
    !tenantId ? [] :
    getCachedCategoriesSync(tenantId) as unknown as MenuCategoryRecord[];
  const menuItems: MenuItemRecord[] =
    (itemsQuery.data && itemsQuery.data.length > 0) ? itemsQuery.data :
    !tenantId ? [] :
    getCachedMenuItemsSync(tenantId) as unknown as MenuItemRecord[];

  // Cache categories to IndexedDB
  useEffect(() => {
    if (catsQuery.data && catsQuery.data.length > 0 && tenantId) {
      cacheCategories(tenantId, catsQuery.data.map((c) => ({
        id: c.id, tenant_id: tenantId, name: c.name,
        sort_order: c.sort_order ?? 0, cached_at: Date.now(),
      })));
    }
  }, [catsQuery.data, tenantId]);

  // Cache menu items to IndexedDB on successful fetch (offline fallback)
  useEffect(() => {
    if (itemsQuery.data && itemsQuery.data.length > 0 && tenantId) {
      cacheMenuItems(tenantId, itemsQuery.data.map((i) => ({
        id: i.id, tenant_id: tenantId, name: i.name,
        base_price: Number(i.base_price), category_id: i.category_id ?? null,
        image_url: i.image_url ?? null, cached_at: Date.now(),
      })));
    }
  }, [itemsQuery.data, tenantId]);


  const [categoryId, setCategoryId] = useState<string | "all">("all");
  const [cart, setCart] = useState<CartLine[]>(() => getCartSync());
  const [payModal, setPayModal] = useState(false);

  // Customer
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerResults, setCustomerResults] = useState<CustomerRecord[]>([]);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustPhone, setNewCustPhone] = useState("");
  const [newCustAddress, setNewCustAddress] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "error" | "success" } | null>(null);
  const showToast = useCallback((message: string, type: "error" | "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Cart: persist to IndexedDB on every change (also syncs memory cache via saveCart)
  useEffect(() => { saveCart(cart); }, [cart]);

  const reachable = useBackendReachable();

  // Customer search (debounced)
  useEffect(() => {
    if (!customerName.trim() || customerName.length < 2 || !tenantId) { setCustomerResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const { customers } = await listCustomers({ tenantId, search: customerName, limit: 5 });
        if (customers.length === 1 && customers[0]!.name.toLowerCase() === customerName.trim().toLowerCase()) {
          selectCustomer(customers[0]!);
        }
        setCustomerResults(customers);
      } catch { setCustomerResults([]); }
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [customerName, tenantId]);

  const selectCustomer = useCallback((c: CustomerRecord) => {
    setCustomerName(c.name);
    setCustomerPhone(c.phone || "");
    setCustomerAddress(c.address_line || c.address || "");
    setCustomerId(c.id);
    setCustomerResults([]);
  }, []);

  const handleAddCustomer = useCallback(async () => {
    if (!customerName.trim()) return;
    const customerInput = {
      tenantId,
      name: customerName.trim(),
      phone: newCustPhone.trim() || customerName.trim(),
      addressLine: newCustAddress.trim() || undefined,
      source: "walkin" as const,
    };

    if (!reachable) {
      // Offline: queue customer creation, apply optimistically
      queueAction("create_customer", { input: customerInput });
      setShowAddCustomer(false);
      showToast(t("pos.customerAdded"), "success");
      return;
    }

    try {
      const c = await createCustomer(customerInput);
      setCustomerId(c.id);
      setCustomerPhone(c.phone || "");
      setCustomerAddress(c.address_line || "");
      setShowAddCustomer(false);
      showToast(t("pos.customerAdded"), "success");
    } catch (err: any) {
      if (err?.name === "DuplicatePhoneError") {
        showToast(t("pos.duplicatePhone"), "error");
      } else {
        showToast(t("pos.addCustomerFailed"), "error");
      }
    }
  }, [customerName, newCustPhone, newCustAddress, tenantId, reachable, showToast]);

  const filteredItems = useMemo(() => {
    if (categoryId === "all") return menuItems;
    return menuItems.filter((i) => i.category_id === categoryId);
  }, [menuItems, categoryId]);

  const qtyFor = useCallback((id: string) => cart.find((l) => l.menu_item_id === id)?.quantity ?? 0, [cart]);
  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);
  const subtotal = cart.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  const addItem = useCallback((item: MenuItemRecord) => {
    beep();
    setCart((prev) => {
      const ex = prev.find((l) => l.menu_item_id === item.id);
      if (ex) return prev.map((l) => l.menu_item_id === item.id ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, { menu_item_id: item.id, item_name: item.name, quantity: 1, unit_price: Number(item.base_price), image_url: item.image_url }];
    });
  }, []);

  const changeQty = useCallback((menuItemId: string, delta: number) => {
    beep();
    setCart((prev) => prev.map((l) => l.menu_item_id === menuItemId ? { ...l, quantity: l.quantity + delta } : l).filter((l) => l.quantity > 0));
  }, []);

  const removeLine = useCallback((menuItemId: string) => {
    beep();
    setCart((prev) => prev.filter((l) => l.menu_item_id !== menuItemId));
  }, []);

  const handlePay = useCallback(async (method: "cash" | "card") => {
    beep();
    setPayModal(false);

    const paymentMethod = method === "cash" ? "cod" : "pay_at_pickup";
    const name = customerName.trim() || t("pos.walkInCustomer");

    const input = {
      customer_name: name,
      customer_phone: customerPhone || null,
      customer_address: customerAddress || null,
      customer_id: customerId,
      fulfillment_type: "pickup" as const,
      payment_method: paymentMethod as "cod" | "pay_at_pickup",
      currency,
      autoAccept: true,
      items: cart.map((line) => ({
        menu_item_id: line.menu_item_id,
        item_name: line.item_name,
        quantity: line.quantity,
        unit_price: line.unit_price,
      })),
    };

    // Offline-first: if backend is unreachable, queue immediately — no error, no alert
    if (!reachable) {
      queueAction("create_order", {
        tenantId,
        input,
        idempotency_key: crypto.randomUUID(),
      });
      showToast(t("pos.orderQueued"), "success");
      setCart([]);
      clearCart();
      setCustomerName("");
      setCustomerPhone("");
      setCustomerAddress("");
      setCustomerId(null);
      return;
    }

    // Online: try direct API call
    try {
      await createStaffOrder(tenantId, input);
      showToast(t("pos.orderCreated"), "success");
    } catch (err: any) {
      // Any failure when "reachable" = backend may have dropped mid-request.
      // Queue the order so it is never lost. This is NOT an error for the user.
      queueAction("create_order", { tenantId, input, idempotency_key: crypto.randomUUID() });
      showToast(t("pos.orderQueued"), "success");
    }

    // Always clear cart after handling (order is either created or queued)
    setCart([]);
    clearCart();
    setCustomerName("");
    setCustomerPhone("");
    setCustomerAddress("");
    setCustomerId(null);
  }, [cart, currency, tenantId, reachable, customerName, customerPhone, customerAddress, customerId, showToast]);

  if (!tenantId) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 12, color: "var(--text-muted)" }}><p style={{ fontSize: 16, fontWeight: 600 }}>{t("pos.notAuth")}</p><p style={{ fontSize: 13 }}>{t("pos.notAuthHint")}</p></div>;
  }

  return (
    <div className="order-pos-body order-pos-body--embed" style={{ height: "100%" }}>
      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 300,
          padding: "12px 28px", borderRadius: "var(--radius-full)",
          background: toast.type === "error" ? "var(--danger)" : "var(--success)",
          color: "#fff", fontWeight: 700, fontSize: 15,
          boxShadow: "0 4px 24px rgba(0,0,0,0.4)", whiteSpace: "nowrap",
          pointerEvents: "none",
        }}>
          {toast.message}
        </div>
      )}
      {/* CATALOG */}
      <section className="order-pos-catalog">
        <div className="order-pos-cats" role="tablist">
          <button type="button" role="tab" aria-selected={categoryId === "all"} className={`order-pos-cat ${categoryId === "all" ? "order-pos-cat--active" : ""}`} onClick={() => { beep(); setCategoryId("all") }}>{t("pos.all")}</button>
          {categories.map((cat) => (
            <button key={cat.id} type="button" role="tab" aria-selected={categoryId === cat.id} className={`order-pos-cat ${categoryId === cat.id ? "order-pos-cat--active" : ""}`} onClick={() => { beep(); setCategoryId(cat.id) }}>{cat.name}</button>
          ))}
        </div>
        <div className="order-pos-grid">
          {itemsQuery.isLoading && menuItems.length === 0 ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="order-pos-card skeleton" />)
            : filteredItems.length === 0 ? <div className="order-pos-empty-catalog"><p>{t("pos.noItems")}</p></div>
              : filteredItems.map((item) => {
                const qty = qtyFor(item.id);
                return (
                  <button key={item.id} type="button" className={`order-pos-card ${qty > 0 ? "order-pos-card--in-cart" : ""}`} onClick={() => addItem(item)}>
                    <div className="order-pos-card__image">
                      {item.image_url ? <img src={item.image_url} alt="" loading="lazy" /> : <ImageIcon className="h-6 w-6 text-[var(--text-muted)]" />}
                      {qty > 0 && <span className="order-pos-card__badge">{qty}</span>}
                    </div>
                    <div className="order-pos-card__info">
                      <p className="order-pos-card__name">{item.name}</p>
                      <div className="order-pos-card__row">
                        <p className="order-pos-card__price">{formatCurrency(Number(item.base_price), currency, "ar")}</p>
                        <span className="order-pos-card__add"><Plus className="h-4 w-4" strokeWidth={2.5} /></span>
                      </div>
                    </div>
                  </button>
                );
              })}
        </div>
      </section>

      {/* TICKET */}
      <aside className="order-pos-ticket">
        <div className="order-pos-ticket__head"><ShoppingBag className="h-4 w-4 text-[var(--accent)]" strokeWidth={2.5} /><h3>{t("pos.cart")}</h3><span className="order-pos-ticket__count">{cartCount}</span></div>

        {/* ── Customer row (tap for modal) ── */}
        <button
          onClick={() => { beep(); setCustomerOpen(true); }}
          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid var(--border-subtle)", background: "none", cursor: "pointer", textAlign: "left" }}
        >
          <div style={{ width: 32, height: 32, borderRadius: 10, background: customerId ? "var(--success)" : "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {customerId ? <Check size={16} style={{ color: "#fff" }} /> : <UserPlus size={16} style={{ color: "var(--accent)" }} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "var(--text-primary)" }}>{customerName || t("pos.walkInCustomer")}</span>
            {customerPhone && <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-muted)" }}>{customerPhone}</span>}
          </div>
          <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: 10 }}>▸</span>
        </button>

        {cart.length === 0 ? <div className="order-pos-ticket__empty">
          <div className="pos-cart-empty-anim">
            <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="pos-cart-empty-svg">
              <path d="M20 22L24 12H40L44 22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="pos-cart-empty-body" />
              <path d="M16 22H48V48C48 50.2091 46.2091 52 44 52H20C17.7909 52 16 50.2091 16 48V22Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" className="pos-cart-empty-body" />
              <circle cx="22" cy="28" r="2" fill="currentColor" className="pos-cart-empty-dot" style={{ animationDelay: "0s" }} />
              <circle cx="32" cy="28" r="2" fill="currentColor" className="pos-cart-empty-dot" style={{ animationDelay: "0.3s" }} />
              <circle cx="42" cy="28" r="2" fill="currentColor" className="pos-cart-empty-dot" style={{ animationDelay: "0.6s" }} />
            </svg>
          </div>
          <p>{t("pos.emptyCart")}</p>
        </div>
          : <ul className="order-pos-ticket__list">
            {cart.map((line) => (
              <li key={line.menu_item_id} className="order-pos-ticket__row" style={{ gap: 8 }}>
                {line.image_url && <img src={line.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />}
                <div className="min-w-0 flex-1">
                  <p className="order-pos-ticket__name">{line.item_name}</p>
                  <p className="order-pos-ticket__unit">{formatCurrency(line.unit_price, currency, "ar")}</p>
                </div>
                <div className="order-pos-ticket__qty">
                  <button type="button" className="order-pos-ticket__qty-btn" onClick={() => changeQty(line.menu_item_id, -1)}><Minus className="h-3.5 w-3.5" /></button>
                  <span>{line.quantity}</span>
                  <button type="button" className="order-pos-ticket__qty-btn" onClick={() => changeQty(line.menu_item_id, 1)}><Plus className="h-3.5 w-3.5" /></button>
                </div>
                <button type="button" onClick={() => removeLine(line.menu_item_id)} style={{ background: "none", border: "none", color: "var(--danger)", cursor: "pointer", padding: 4, flexShrink: 0 }}><Trash2 size={16} /></button>
              </li>
            ))}
          </ul>}
        {cart.length > 0 && (
          <div style={{ borderTop: "1px solid var(--border-subtle)", padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>{t("pos.total")}</span>
              <span style={{ fontWeight: 900, fontSize: 18, color: "var(--accent)" }}>{formatCurrency(subtotal, currency, "ar")}</span>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => { beep(); window.print() }} style={{ flex: 1, padding: "12px", borderRadius: "var(--radius-md)", background: "var(--surface-hover)", color: "var(--text-primary)", border: "1px solid var(--border)", fontWeight: 700, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Printer size={16} />{t("pos.print")}</button>
              <button onClick={() => { beep(); setPayModal(true) }} style={{ flex: 2, padding: "12px", borderRadius: "var(--radius-md)", background: "var(--accent)", color: "#fff", border: "none", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>{t("pos.pay")} • {formatCurrency(subtotal, currency, "ar")}</button>
            </div>
          </div>
        )}
      </aside>

      {/* PAYMENT MODAL */}
      {payModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)" }} onClick={() => setPayModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-xl)", padding: "28px 24px", width: 340, maxWidth: "92vw", boxShadow: "var(--shadow-popover)", textAlign: "center" }}>
            <button onClick={() => setPayModal(false)} style={{ position: "absolute", top: 12, right: 12, background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}><X size={20} /></button>
            <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 900, color: "var(--text-primary)" }}>{t("pos.payModal.title")}: {formatCurrency(subtotal, currency, "ar")}</h2>
            <p style={{ margin: "0 0 20px", fontSize: 13, color: "var(--text-muted)" }}>{t("pos.payModal.subtitle")}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={() => handlePay("cash")} style={{ width: "100%", padding: "14px", borderRadius: "var(--radius-md)", background: "var(--success)", color: "#fff", border: "none", fontWeight: 800, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Banknote size={20} />{t("pos.payModal.cash")}</button>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER MODAL */}
      {customerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 110, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.55)" }} onClick={() => { setCustomerOpen(false); setShowAddCustomer(false); setCustomerResults([]); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface-raised)", borderRadius: "var(--radius-xl)", padding: "24px", width: 400, maxWidth: "92vw", maxHeight: "80vh", overflowY: "auto", boxShadow: "var(--shadow-popover)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <UserPlus size={20} style={{ color: "var(--accent)" }} />
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: "var(--text-primary)" }}>{t("pos.customer")}</h2>
              <div style={{ flex: 1 }} />
              <button onClick={() => { setCustomerOpen(false); setShowAddCustomer(false); setCustomerResults([]); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}><X size={20} /></button>
            </div>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input value={customerName} onChange={(e) => { setCustomerName(e.target.value); setCustomerId(null); setShowAddCustomer(false); }} placeholder={t("pos.customerName")} autoFocus
                style={{ width: "100%", padding: "14px 14px 14px 40px", borderRadius: "var(--radius-lg)", border: "2px solid var(--border)", background: "var(--surface-muted)", color: "var(--text-primary)", fontSize: 16, fontWeight: 600, outline: "none", boxSizing: "border-box" }} />
            </div>
            {customerResults.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {customerResults.map((c) => (
                  <button key={c.id} onClick={() => { selectCustomer(c); setCustomerOpen(false); }}
                    style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px", border: "none", borderBottom: "1px solid var(--border-subtle)", background: "none", cursor: "pointer", textAlign: "left", borderRadius: "var(--radius-md)" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "var(--accent)", flexShrink: 0 }}>{c.name.charAt(0).toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)" }}>{c.name}</div>
                      {c.phone && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{c.phone}</div>}
                    </div>
                    {c.address_line && <div style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: "auto", textAlign: "right", maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.address_line}</div>}
                  </button>
                ))}
              </div>
            )}
            {customerName.trim() && customerResults.length === 0 && !customerId && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: "var(--text-muted)", textAlign: "center", padding: "12px 0" }}>{t("pos.noCustomerFound")}</div>
                {!showAddCustomer ? (
                  <button onClick={() => setShowAddCustomer(true)}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", padding: "16px", borderRadius: "var(--radius-lg)", border: "2px dashed var(--accent)", background: "var(--accent-light)", color: "var(--accent)", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>
                    <UserPlus size={20} /> {t("pos.addCustomer")}
                  </button>
                ) : (
                  <div style={{ padding: 16, background: "var(--surface-muted)", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)" }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)", marginBottom: 12 }}>{t("pos.newCustomer")}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>{t("pos.customerName")}</div>
                        <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "var(--surface-raised)", color: "var(--text-primary)", fontSize: 15, fontWeight: 600, outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>{t("pos.customerPhone")} <span style={{ fontWeight: 400 }}>({t("pos.optional")})</span></div>
                        <input value={newCustPhone} onChange={(e) => setNewCustPhone(e.target.value)} placeholder="+60..." style={{ width: "100%", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "var(--surface-raised)", color: "var(--text-primary)", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>{t("pos.customerAddress")} <span style={{ fontWeight: 400 }}>({t("pos.optional")})</span></div>
                        <input value={newCustAddress} onChange={(e) => setNewCustAddress(e.target.value)} style={{ width: "100%", padding: "12px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "var(--surface-raised)", color: "var(--text-primary)", fontSize: 15, outline: "none", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
                        <button onClick={() => setShowAddCustomer(false)} style={{ flex: 1, padding: "14px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)", background: "none", color: "var(--text-muted)", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>{t("pos.cancel")}</button>
                        <button onClick={handleAddCustomer} style={{ flex: 2, padding: "14px", borderRadius: "var(--radius-md)", border: "none", background: "var(--accent)", color: "#fff", fontWeight: 800, fontSize: 15, cursor: "pointer" }}>{t("pos.save")}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {customerName.trim() && !customerId && customerResults.length === 0 && !showAddCustomer && (
              <button onClick={() => setCustomerOpen(false)}
                style={{ width: "100%", padding: "14px", borderRadius: "var(--radius-lg)", border: "1px solid var(--border)", background: "none", color: "var(--text-secondary)", fontWeight: 700, fontSize: 15, cursor: "pointer" }}>
                {t("pos.continueWithName")} "{customerName.trim()}"
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
